import type { useSnackBar } from '@usealloy/component-library';
import type { PublicId, WorkflowProps } from 'flow/store/engine/types';
import type { AppDispatch } from '~/store';
import getRemoteCurrentAndNextLocalSnippets from './getRemoteCurrentAndNextLocalSnippets';
import { setSnippetCollapsed, setSnippetMode } from 'flow/store/redux/slice';
import { upgradeLinkedSnippetVersionThunk } from 'flow/store/redux/thunks/snippets';
import type { API } from '~/helpers/api-new/modules';
import { snippetMode } from 'flow/store/redux/types';

export interface UpgradeLinkedSnippetVersionArgs {
  addSnackBar: ReturnType<typeof useSnackBar>;
  currentVersion: number;
  dispatch: AppDispatch;
  libraryApi: API['library'];
  localSnippetActionPublicId: PublicId;
  nextVersion: number;
  snippetToken: string;
  typhonDomain: string;
  workflowProps: WorkflowProps;
}

/**
 * Fetches current/next remote snippet versions, dispatches the upgrade thunk,
 * and shows an error snackbar on failure. Used by node dropdown and UpdateSnippetDropdown.
 */
export default async function upgradeLinkedSnippetVersion({
  addSnackBar,
  currentVersion,
  dispatch,
  libraryApi,
  localSnippetActionPublicId,
  nextVersion,
  snippetToken,
  typhonDomain,
  workflowProps,
}: UpgradeLinkedSnippetVersionArgs): Promise<void> {
  try {
    // Enter paste mode here so spinner shows while fetching
    dispatch(setSnippetMode({ mode: snippetMode.pastePending }));

    const {
      remoteCurrentSnippet,
      remoteNextSnippet,
    } = await getRemoteCurrentAndNextLocalSnippets({
      currentVersion,
      libraryApi,
      nextVersion,
      snippetToken,
      typhonDomain,
      workflowProps,
    });

    await dispatch(upgradeLinkedSnippetVersionThunk({
      currentRemoteSnippet: remoteCurrentSnippet,
      localSnippetActionPublicId,
      nextRemoteSnippet: remoteNextSnippet,
    }));

    dispatch(setSnippetCollapsed({ isCollapsed: false, snippetId: localSnippetActionPublicId }));
  } catch {
    dispatch(setSnippetMode({ mode: null }));
    addSnackBar({
      title: 'Error updating snippet version',
      type: 'danger',
      duration: 5_000,
    });
  }
}
